import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { mockData } from '../mock';

// Clean Training Structure Section (No emojis, clean accordions)
export const CleanTrainingStructureSection = () => (
  <section className="py-24 bg-gray-50">
    <div className="container mx-auto px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-medium text-gray-800 mb-6">
            Estrutura da Formação
          </h2>
          <p className="text-lg text-gray-600 font-light mb-8">
            5 <strong className="font-semibold">Módulos Intensivos</strong> Ao Vivo
          </p>
          <div className="inline-block bg-green-50 text-green-700 px-6 py-2 rounded-sm border border-green-200">
            Formação presencial online
          </div>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {mockData.modules.map((module, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`} 
              className="rounded-sm border border-blue-300/60 shadow-lg hover:shadow-xl hover:shadow-blue-500/20 transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 15%, #bfdbfe 30%, #dbeafe 45%, #e0e7ff 60%, #e2e8f0 75%, #f1f5f9 90%, #e2e8f0 100%)'
              }}
            >
              <AccordionTrigger className="text-left hover:no-underline py-8 px-8">
                <div className="flex items-center gap-6 w-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-slate-600 rounded-sm flex items-center justify-center text-white font-semibold flex-shrink-0 shadow-md">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    {/* Small accent line */}
                    <div className="w-8 h-0.5 bg-gradient-to-r from-blue-600 to-slate-500 rounded-full mb-3"></div>
                    
                    <h3 className="text-xl font-medium text-gray-800 mb-2 text-left">
                      {module.title}
                    </h3>
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>{module.duration}</span>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-8">
                <div className="pt-6 border-t border-blue-300/40">
                  {module.objective && (
                    <div className="mb-6 p-6 bg-white/40 backdrop-blur-sm rounded-sm border border-blue-200/50">
                      <p className="text-sm font-medium text-gray-700 mb-2">Objetivo:</p>
                      <p className="text-sm text-gray-600 leading-relaxed font-light italic">
                        {module.objective}
                      </p>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    {module.content.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start gap-3 py-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 font-light text-sm leading-relaxed">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="text-center mt-12">
          <button className="bg-white hover:bg-gray-50 text-gray-800 px-8 py-3 rounded-sm border border-gray-300 font-medium transition-all duration-300 shadow-sm hover:shadow-md">
            Baixar programa completo
          </button>
        </div>
      </div>
    </div>
  </section>
);

// Exclusive Materials Section - Premium Library Gallery
export const CleanExclusiveMaterialsSection = () => (
  <section className="py-24 bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
    {/* Subtle background pattern */}
    <div className="absolute inset-0 opacity-5">
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>
    </div>
    
    <div className="container mx-auto px-8 relative z-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-2 rounded-full border border-white/20">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-white font-light text-sm tracking-wide">
                {mockData.exclusiveMaterials.title}
              </span>
            </div>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            {mockData.exclusiveMaterials.mainTitle}
          </h2>
          
          <p className="text-xl text-gray-200 font-light max-w-4xl mx-auto leading-relaxed">
            Esta formação entrega um corpo de conhecimento técnico e clínico construído ao longo de <strong className="font-semibold">décadas de pesquisa interdisciplinar entre neurociência, psicossomática e medicina germânica</strong>.
          </p>
        </div>

        {/* Materials Grid */}
        <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-8">
          {mockData.exclusiveMaterials.materials.map((material, index) => (
            <div 
              key={index}
              className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-8 hover:bg-white/10 hover:border-white/20 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2 transition-all duration-500 cursor-pointer"
            >
              <div className="flex gap-6 items-start">
                {/* Icon or Custom Image - Left Side */}
                <div className="flex-shrink-0">
                  {material.iconImage ? (
                    <div className="group-hover:scale-110 transition-transform duration-300">
                      <img 
                        src={material.iconImage} 
                        alt={material.title}
                        className="w-32 h-32 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="text-8xl group-hover:scale-110 transition-transform duration-300">
                      {material.icon}
                    </div>
                  )}
                </div>
                
                {/* Content - Right Side */}
                <div className="flex-1 min-w-0">
                  {/* Title */}
                  <h3 className="text-xl font-medium text-white mb-3 leading-snug group-hover:text-blue-300 transition-colors duration-300">
                    {material.title}
                  </h3>
                  
                  {/* Pages */}
                  <div className="inline-block bg-blue-500/20 text-blue-300 px-4 py-1 rounded-full text-sm font-light mb-4 border border-blue-400/30">
                    {material.pages}
                  </div>
                  
                  {/* Description */}
                  <p className="text-gray-300 font-light leading-relaxed text-sm">
                    {material.description}
                  </p>
                  
                  {/* Decorative accent line */}
                  <div className="mt-6 w-12 h-0.5 bg-gradient-to-r from-blue-400 to-transparent rounded-full group-hover:w-24 transition-all duration-500"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom decorative element */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white/5 backdrop-blur-sm px-8 py-4 rounded-sm border border-white/10">
            <p className="text-gray-300 font-light text-lg">
              <span className="text-blue-300 font-medium">+2.000 páginas</span> de conteúdo científico e clínico exclusivo
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Clean Testimonials Section - Students + Patient Case
export const CleanTestimonialsSection = () => (
  <section className="py-24 bg-white">
    <div className="container mx-auto px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-medium text-gray-800 mb-6">
            Resultados Reais e Depoimentos
          </h2>
          <p className="text-lg text-gray-600 font-light max-w-3xl mx-auto">
            Transformações de alunos e resultados clínicos documentados
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Student Testimonials */}
          {mockData.testimonials.map((testimonial, index) => (
            <div 
              key={index} 
              className="p-8 rounded-lg border border-blue-300/60 shadow-lg hover:shadow-xl transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 15%, #bfdbfe 30%, #dbeafe 45%, #e0e7ff 60%, #e2e8f0 75%, #f1f5f9 90%, #e2e8f0 100%)'
              }}
            >
              <div className="space-y-6">
                {/* Badge */}
                <div className="flex justify-between items-start">
                  <div className="inline-block bg-blue-600 text-white px-4 py-1 rounded-full text-xs font-medium">
                    Aluno Formado
                  </div>
                  <div className="w-8 h-1 bg-gradient-to-r from-blue-600 to-slate-500 rounded-full"></div>
                </div>
                
                {/* Photo and Name */}
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-20 h-20 rounded-lg object-cover border-2 border-white/50 shadow-md"
                  />
                  <div>
                    <p className="font-semibold text-gray-800 text-lg">
                      {testimonial.name}
                    </p>    
                    <p className="text-sm text-gray-600 font-medium">
                      {testimonial.profession}
                    </p>
                  </div>
                </div>
                
                {/* Testimonial */}
                <p className="text-gray-700 leading-relaxed font-light italic text-sm">
                  "{testimonial.testimonial}"
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Clinical Cases Section */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Existing Clinical Case - Tumor */}
          <div 
            className="p-8 rounded-lg border border-blue-400/70 shadow-xl hover:shadow-2xl transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 20%, #93c5fd 40%, #dbeafe 60%, #e0e7ff 80%, #dbeafe 100%)'
            }}
          >
            <div className="space-y-6">
              {/* Badge */}
              <div className="flex justify-between items-start">
                <div className="inline-block bg-green-600 text-white px-4 py-1 rounded-full text-xs font-medium">
                  Caso Clínico Real
                </div>
                <div className="w-8 h-1 bg-gradient-to-r from-green-600 to-blue-600 rounded-full"></div>
              </div>
              
              {/* Title */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {mockData.clinicalCase.description}
                </h3>
                <p className="text-sm text-gray-600 font-medium">
                  {mockData.clinicalCase.subtitle}
                </p>
              </div>
              
              {/* Clinical Image */}
              <div className="rounded-lg overflow-hidden shadow-lg border-2 border-white/50">
                <img 
                  src={mockData.clinicalCase.image}
                  alt={mockData.clinicalCase.description}
                  className="w-full h-auto object-contain bg-white"
                />
              </div>
            </div>
          </div>

          {/* New Clinical Case - Obesity */}
          {mockData.clinicalCases && mockData.clinicalCases.map((clinicalCase, index) => (
            <div 
              key={index}
              className="p-8 rounded-lg border border-green-400/70 shadow-xl hover:shadow-2xl transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 20%, #6ee7b7 40%, #d1fae5 60%, #dbeafe 80%, #d1fae5 100%)'
              }}
            >
              <div className="space-y-6">
                {/* Badge */}
                <div className="flex justify-between items-start">
                  <div className="inline-block bg-green-600 text-white px-4 py-1 rounded-full text-xs font-medium">
                    Caso Clínico Real
                  </div>
                  <div className="w-8 h-1 bg-gradient-to-r from-green-600 to-blue-600 rounded-full"></div>
                </div>
                
                {/* Title */}
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {clinicalCase.title}
                  </h3>
                  <p className="text-sm text-gray-600 font-medium">
                    {clinicalCase.subtitle}
                  </p>
                </div>
                
                {/* Description */}
                <p className="text-gray-700 leading-relaxed font-light text-sm">
                  {clinicalCase.description}
                </p>
                
                {/* Clinical Image */}
                <div className="rounded-lg overflow-hidden shadow-lg border-2 border-white/50">
                  <img 
                    src={clinicalCase.image}
                    alt={clinicalCase.title}
                    className="w-full h-auto object-contain bg-white"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Clean Investment Section with DNA Background
export const CleanInvestmentSection = () => (
  <section 
    className="py-24 relative text-white"
    style={{
      backgroundImage: `url('${mockData.scientificImages.dna}')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}
  >
    {/* Dark blue overlay with gradient */}
    <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-slate-800/70 to-blue-900/90"></div>
    
    <div className="relative z-10 container mx-auto px-8">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-block bg-blue-500/80 backdrop-blur-sm text-white px-6 py-2 rounded-sm mb-4 border border-blue-400/30">
          {mockData.investment.highlight}
        </div>
        
        {/* New badge for next module */}
        <div className="inline-block bg-green-500/80 backdrop-blur-sm text-white px-6 py-2 rounded-sm mb-8 border border-green-400/30">
          <strong className="font-semibold">Próximo Módulo I:</strong> 07/02/26
        </div>
        
        <h2 className="text-3xl lg:text-4xl font-medium mb-8">
          Investimento
        </h2>

        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-sm max-w-lg mx-auto p-8 shadow-2xl relative">
          {/* Black Friday Sticker */}
          <div className="absolute -top-4 -right-4 z-20">
            <img 
              src="https://customer-assets.emergentagent.com/job_therapia-web/artifacts/4wyw5dx2_Untitled%20design%20%2851%29.png"
              alt="Black Friday"
              className="w-24 h-24 object-contain drop-shadow-2xl transform rotate-12 hover:rotate-0 transition-transform duration-300"
              style={{
                filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))'
              }}
            />
          </div>
          
          <div className="text-center space-y-6">
            <div>
              <p className="text-lg text-white/60 line-through font-light mb-2">
                De R$ 5.500
              </p>
              <p className="text-4xl lg:text-5xl font-semibold text-blue-300 mb-3">
                12x de R$ 291,67
              </p>
              <p className="text-base text-white/70 font-light">
                ou R$ 3.500 à vista
              </p>
            </div>
            
            <a 
              href="https://chk.eduzz.com/39VE7ZGDWR"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-sm font-medium text-lg transition-all duration-300 border-0 shadow-lg text-center"
            >
              Garantir minha vaga
            </a>
            
            {/* Subtitle text */}
            <p className="text-xs text-white/50 font-light italic leading-relaxed">
              Estude antes, integre melhor: acesse o material didático e potencialize seu aproveitamento no módulo ao vivo.
            </p>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
          <div className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <span className="font-light">Certificação AEMP</span>
          </div>
          <div className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <span className="font-light">Suporte por 1 ano</span>
          </div>
          <div className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <span className="font-light">Acesso vitalício à comunidade</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Clean Registration Form
export const CleanRegistrationFormSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-blue-900 to-slate-900">
      <div className="container mx-auto px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <h2 className="text-3xl lg:text-5xl font-medium text-white mb-6">
              Transforme sua prática profissional
            </h2>
            <p className="text-xl text-gray-200 font-light max-w-2xl mx-auto leading-relaxed">
              Garanta sua vaga na última turma com valor promocional. Formação completa em Terapia Psicossomática Direta® com certificação internacional AEMP.
            </p>
          </div>

          <div className="space-y-8">
            <div className="relative inline-block">
              <a 
                href="https://chk.eduzz.com/39VE7ZGDWR"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-16 py-6 rounded-sm font-semibold text-2xl transition-all duration-300 shadow-2xl hover:shadow-green-500/25 border-0 hover:scale-105"
              >
                Inscreva-se agora
              </a>
              {/* Black Friday Sticker */}
              <img 
                src="https://customer-assets.emergentagent.com/job_therapia-web/artifacts/4wyw5dx2_Untitled%20design%20%2851%29.png"
                alt="Black Friday"
                className="absolute -top-4 -right-4 w-20 h-20 object-contain drop-shadow-2xl transform rotate-12"
                style={{
                  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.4))'
                }}
              />
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-gray-300 text-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Certificação Internacional</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>100% ao vivo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Última turma promocional</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Clean FAQ Section - Sophisticated Accordion Design
export const CleanFAQSection = () => (
  <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
    <div className="container mx-auto px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-3 bg-blue-500/10 backdrop-blur-sm px-6 py-2 rounded-full border border-blue-500/20">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-blue-700 font-medium text-sm tracking-wide">
                Perguntas Frequentes
              </span>
            </div>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-medium text-gray-800 mb-6 leading-tight">
            Dúvidas sobre a formação
          </h2>
          
          <p className="text-lg text-gray-600 font-light max-w-2xl mx-auto leading-relaxed">
            Tudo o que você precisa saber antes de garantir sua vaga
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {mockData.faq.map((item, index) => (
            <AccordionItem 
              key={index} 
              value={`faq-${index}`}
              className="bg-white border border-gray-200 rounded-sm shadow-sm hover:shadow-md transition-all duration-300"
            >
              <AccordionTrigger className="px-8 py-6 text-left hover:no-underline group">
                <div className="flex items-start gap-4 w-full">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm mt-1">
                    {index + 1}
                  </div>
                  <span className="text-lg font-medium text-gray-800 group-hover:text-blue-600 transition-colors duration-300 flex-1 pr-4">
                    {item.question}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <div className="pl-12 pr-4">
                  <div className="h-px w-12 bg-gradient-to-r from-blue-500 to-transparent mb-4"></div>
                  <p className="text-gray-600 leading-relaxed font-light">
                    {item.answer}
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-50 via-gray-50 to-blue-50 p-8 rounded-sm border border-blue-200/50">
            <p className="text-gray-700 font-light text-lg mb-4">
              Ainda tem dúvidas? Entre em contato conosco!
            </p>
            <a 
              href={`https://wa.me/${mockData.contact.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Clean Footer
export const CleanFooter = () => (
  <footer className="bg-gray-900 text-white py-16">
    <div className="container mx-auto px-8">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <h3 className="text-2xl font-medium mb-6 text-green-400">
              Mentoria TPD®
            </h3>
            <p className="text-gray-300 leading-relaxed font-light">
              Transforme sua prática profissional com metodologia científica 
              em Terapia Psicossomática Direta.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-medium mb-6 text-green-400">Contato Profissional</h4>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <a 
                  href={`https://wa.me/${mockData.contact.whatsapp.replace('+', '')}`}
                  className="text-gray-300 hover:text-white transition-colors font-light"
                >
                  {mockData.contact.whatsapp}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <a 
                  href={`mailto:${mockData.contact.email}`}
                  className="text-gray-300 hover:text-white transition-colors font-light"
                >
                  {mockData.contact.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <a 
                  href={`https://instagram.com/${mockData.contact.instagram.replace('@', '')}`}
                  className="text-gray-300 hover:text-white transition-colors font-light"
                >
                  {mockData.contact.instagram}
                </a>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-medium mb-6 text-green-400">Navegação</h4>
            <div className="space-y-3">
              <a href="#sobre" className="block text-gray-300 hover:text-white transition-colors font-light">
                Sobre a formação
              </a>
              <a href="#modulos" className="block text-gray-300 hover:text-white transition-colors font-light">
                Módulos
              </a>
              <a href="#investimento" className="block text-gray-300 hover:text-white transition-colors font-light">
                Investimento
              </a>
              <a href="#contato" className="block text-gray-300 hover:text-white transition-colors font-light">
                Contato
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-12 pt-8 text-center">
          <p className="text-gray-400 font-light">
            © 2025 Mentoria TPD - Flor Dirube. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

export default { 
  CleanTrainingStructureSection,
  CleanExclusiveMaterialsSection, 
  CleanTestimonialsSection, 
  CleanInvestmentSection, 
  CleanRegistrationFormSection,
  CleanFAQSection, 
  CleanFooter 
};